import NextHead from 'next/head'
import { useEffect } from 'react'
import type {
  FormEvent,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
} from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/enlayout'
import { Head } from '@/components/enlayouts/Head'
import { Layout } from '@/components/enlayouts/Ir'

type PeriodButton = {
  value: string
  label: string
}

type CategoryDataKey = 'cate' | 'seiyu'

type ChartSection = {
  id: string
  tabLabel: ReactNode
  title: ReactNode
  graphLabel: string
  graphId: string
  checkId: string
  sourceGlobal: string
  categoriesGlobal: string
  initialDataKey: string
  buttons: PeriodButton[]
  hasCategoryNav?: boolean
}

const downloadPath = '//irpocket.com/141A/chart__ver2_hc6/data_en.xlsx'
const chartStylesheet = '//irpocket.com/141A/chart_ver2_hc6/chart_en.css'
const jqueryScript =
  '//ajax.googleapis.com/ajax/libs/jquery/3.6.4/jquery.min.js'
const chartFontFamily = '"Noto Sans JP", Arial, sans-serif'
const chartFontOverrideId = 'chart-font-scope-override'

// chart.css sets `font-family` on almost every tag with `!important`. The
// Highcharts graph itself already gets the site font forced back on via
// inline styles in installHighchartsChartFontPatch, so nothing on this page
// is meant to render in the vendor font — re-assert the site font globally
// with a higher-specificity selector (wins regardless of load order).
const installChartFontScopeOverride = () => {
  if (document.getElementById(chartFontOverrideId)) return

  const style = document.createElement('style')
  style.id = chartFontOverrideId
  style.textContent = `*:not(#__chart_font_scope_never_matches__) { font-family: ${chartFontFamily} !important; }`
  document.head.appendChild(style)
}

const chartScripts = [
  '//irpocket.com/141A/chart_ver2_hc6/exporting.js',
  '//irpocket.com/141A/chart_ver2_hc6/data_en.js',
  '//irpocket.com/141A/chart_ver2_hc6/data2_en.js',
  '//irpocket.com/141A/chart_ver2_hc6/data3_en.js',
  '//irpocket.com/141A/chart_ver2_hc6/data4_en.js',
  '//irpocket.com/141A/chart_ver2_hc6/data5_en.js',
  '//irpocket.com/141A/chart_ver2_hc6/data6_en.js',
  '//irpocket.com/141A/chart_ver2_hc6/data7_en.js',
  '//irpocket.com/141A/chart_ver2_hc6/data8_en.js',
  '//irpocket.com/141A/chart_ver2_hc6/add-select-term.js',
  '//irpocket.com/141A/chart_ver2_hc6/main_en.js',
]

const defaultButtons = [
  { value: '-5', label: 'latest 5 years' },
  { value: '-12', label: 'All' },
]
const defaultGraphLabel = 'Chart'

const chartSections: ChartSection[] = [
  {
    id: 'newest',
    tabLabel: 'Financial Performance',
    title: 'Financial Performance',
    graphLabel: defaultGraphLabel,
    graphId: 'graph-newest',
    checkId: 'graph-newest-check',
    sourceGlobal: 'source',
    categoriesGlobal: 'mcategories',
    initialDataKey: 'gyouseki',
    buttons: [
      { value: '-3', label: 'latest 3 years' },
      { value: '-5', label: 'latest 5 years' },
      { value: '-10', label: 'All' },
    ],
  },
  {
    id: 'zaimu',
    tabLabel: 'Financial Position',
    title: 'Financial Position',
    graphLabel: defaultGraphLabel,
    graphId: 'graph-zaimu',
    checkId: 'graph-zaimu-check',
    sourceGlobal: 'zsource',
    categoriesGlobal: 'zmcategories',
    initialDataKey: 'zaimu',
    buttons: defaultButtons,
  },
  {
    id: 'cf',
    tabLabel: 'Cash Flow',
    title: 'Cash Flow',
    graphLabel: defaultGraphLabel,
    graphId: 'graph-cf',
    checkId: 'graph-cf-check',
    sourceGlobal: 'cfsource',
    categoriesGlobal: 'cfmcategories',
    initialDataKey: 'cf',
    buttons: defaultButtons,
  },
  {
    id: 'per',
    tabLabel: 'Per Share Data / Dividends',
    title: 'Per Share Data / Dividends',
    graphLabel: defaultGraphLabel,
    graphId: 'graph-per',
    checkId: 'graph-per-check',
    sourceGlobal: 'psource',
    categoriesGlobal: 'pmcategories',
    initialDataKey: 'per',
    buttons: defaultButtons,
  },
  {
    id: 'share',
    tabLabel: 'Stock Metrics',
    title: 'Stock Metrics',
    graphLabel: defaultGraphLabel,
    graphId: 'graph-share',
    checkId: 'graph-share-check',
    sourceGlobal: 'shsource',
    categoriesGlobal: 'shmcategories',
    initialDataKey: 'share',
    buttons: defaultButtons,
  },
  {
    id: 'store',
    tabLabel: 'Number of Stores',
    title: 'Number of Stores',
    graphLabel: defaultGraphLabel,
    graphId: 'graph-store',
    checkId: 'graph-store-check',
    sourceGlobal: 'stsource',
    categoriesGlobal: 'stmcategories',
    initialDataKey: 'store',
    buttons: defaultButtons,
  },
  {
    id: 'trialseiyu',
    tabLabel: 'TRIAL / SEIYU',
    title: 'TRIAL / SEIYU',
    graphLabel: defaultGraphLabel,
    graphId: 'graph-trialseiyu',
    checkId: 'graph-trialseiyu-check',
    sourceGlobal: 'tsource',
    categoriesGlobal: 'tmcategories',
    initialDataKey: 'trialseiyu',
    buttons: defaultButtons,
  },
  {
    id: 'cate',
    tabLabel: (
      <>
        Net Sales by <br />
        Product Category
      </>
    ),
    title: (
      <>
        Net Sales by <br />
        Product Category
      </>
    ),
    graphLabel: defaultGraphLabel,
    graphId: 'graph-cate',
    checkId: 'graph-cate-check',
    sourceGlobal: 'csource',
    categoriesGlobal: 'cmcategories',
    initialDataKey: 'cate',
    buttons: defaultButtons,
    hasCategoryNav: true,
  },
]

const description = (
  <>
    You can view it in different ways, such as a graph display or a table
    display where you can download just the data.
    <br />
    You can also display numerical values just by clicking on the graph. You can
    also select the number of indicators, so please take advantage of it.
  </>
)

type HighchartsSeries = {
  update?: (options: Record<string, unknown>, redraw?: boolean) => void
  setData?: (data: Array<number | null>, redraw?: boolean) => void
  visible?: boolean
  setVisible?: (visible: boolean, redraw?: boolean) => void
}

type HighchartsAxis = {
  update?: (options: Record<string, unknown>, redraw?: boolean) => void
  setCategories?: (categories: string[], redraw?: boolean) => void
}

type HighchartsChart = {
  destroyed?: boolean
  renderTo?: HTMLElement
  series?: HighchartsSeries[]
  xAxis?: HighchartsAxis[]
  yAxis?: HighchartsAxis[]
  options?: {
    chart?: {
      renderTo?: string | HTMLElement
    }
  }
  update?: (
    options: Record<string, unknown>,
    redraw?: boolean,
    oneToOne?: boolean,
    animation?: boolean
  ) => void
  reflow?: () => void
  redraw?: () => void
  destroy?: () => void
}

type HighchartsConstructor = {
  new (options: Record<string, unknown>): HighchartsChart
  prototype: unknown
}

type HighchartsGlobal = {
  charts?: Array<HighchartsChart | undefined>
  Chart?: HighchartsConstructor
  chartFontPatchInstalled?: boolean
}

type ChartRuntimeWindow = typeof window & {
  jQuery?: (selector: string | HTMLElement) => unknown
  runChart?: (
    chart: unknown,
    element: unknown,
    source: unknown,
    categories: string[],
    renderTo: string,
    current: string
  ) => void
} & Record<string, unknown>

type ChartTarget = ChartSection & {
  panel: HTMLElement
  graph: HTMLElement
  checkContainer: HTMLElement
  source: unknown
  categories: string[]
}

type ChartIndex = {
  name: string
  type?: string
  color?: string
  yAxis?: number
  dp?: number
}

type ChartDataset = {
  title: string
  unit?: string
  unit2?: string
  decimal?: number
  decimal2?: number
  min?: number
  max?: number
  span?: number
  min2?: number
  max2?: number
  span2?: number
  note?: string
  height?: number
  stack?: boolean
  master: Array<Array<string | number | null>>
  indexes: ChartIndex[]
}

type ChartSource = Record<string, ChartDataset>

const isChartTarget = (target: ChartTarget | null): target is ChartTarget =>
  target !== null

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const getChartRenderToId = (chart: HighchartsChart) => {
  if (chart.renderTo?.id) return chart.renderTo.id

  const renderTo = chart.options?.chart?.renderTo
  if (typeof renderTo === 'string') return renderTo
  if (renderTo instanceof HTMLElement) return renderTo.id

  return null
}

const withChartFontStyle = (style: unknown) => ({
  ...(isRecord(style) ? style : {}),
  fontFamily: chartFontFamily,
  fontWeight: 'normal',
})

const withChartAxisFont = (axis: unknown): unknown => {
  if (Array.isArray(axis)) return axis.map(withChartAxisFont)
  if (!isRecord(axis)) return axis

  const labels = isRecord(axis.labels) ? axis.labels : {}

  return {
    ...axis,
    labels: {
      ...labels,
      style: withChartFontStyle(labels.style),
    },
  }
}

const MEASURE_CANVAS =
  typeof document !== 'undefined' ? document.createElement('canvas') : null

const measureItemWidth = (name: string) => {
  const ctx = MEASURE_CANVAS?.getContext('2d')
  if (!ctx) return 0
  ctx.font = `12px ${chartFontFamily}`
  return ctx.measureText(name).width
}

const alignLegendItems = (chartInstance: unknown) => {
  try {
    const chart = chartInstance as Record<string, unknown>
    const legend = chart.legend as Record<string, unknown> | undefined
    const allItems = legend?.allItems as Array<{ legendItem?: { getBBox?: () => { width: number } } }> | undefined
    if (!allItems?.length) return

    const isSP = typeof window !== 'undefined' && window.innerWidth < 768

    if (isSP) {
      // On SP, force a single column by setting itemWidth wide enough
      // that items can't fit side-by-side → they stack vertically.
      const chartWidth = (chart.chartWidth as number) || 0
      const itemWidth = chartWidth > 0 ? chartWidth - 40 : 300
      const currentWidth = (legend?.options as Record<string, unknown> | undefined)?.itemWidth
      if (typeof currentWidth === 'number' && currentWidth === itemWidth) return
      const update = chart.update as Function | undefined
      update?.call(chart, { legend: { itemWidth } }, true, false, false)
      return
    }

    // PC: align columns using Canvas measureText (sync, no font-loading race)
    let maxWidth = 0
    allItems.forEach((item) => {
      const name = (item as Record<string, unknown>).name as string | undefined
      if (name) {
        const w = measureItemWidth(name)
        if (w > maxWidth) maxWidth = w
      }
    })
    if (maxWidth <= 0) return

    // 14 = icon width, 8 = gap between icon and text, 12 = padding
    const itemWidth = Math.ceil(maxWidth) + 14 + 8 + 12

    const availableWidth = (chart.plotWidth as number) || (chart.chartWidth as number) || 0
    // If the longest item is wider than half the chart width it can't fit.
    if (availableWidth > 0 && itemWidth > availableWidth / 2) return

    const currentWidth = (legend?.options as Record<string, unknown> | undefined)?.itemWidth
    if (typeof currentWidth === 'number' && currentWidth === itemWidth) return

    const update = chart.update as Function | undefined
    update?.call(chart, { legend: { itemWidth } }, true, false, false)
  } catch (_) { /* ignore */ }
}

const withChartFontOptions = (options: Record<string, unknown>) => {
  const chart = isRecord(options.chart) ? options.chart : {}
  const legend = isRecord(options.legend) ? options.legend : {}
  const chartEvents = isRecord(chart.events) ? chart.events : {} as Record<string, unknown>
  const originalLoad = typeof chartEvents.load === 'function' ? chartEvents.load : undefined
  const originalRedraw = typeof chartEvents.redraw === 'function' ? chartEvents.redraw : undefined

  return {
    ...options,
    chart: {
      ...chart,
      style: withChartFontStyle(chart.style),
      events: {
        ...chartEvents,
        load: function (this: unknown) {
          if (originalLoad) (originalLoad as Function).call(this)
          // Noto Sans JP font width differs from browser default,
          // so recalculate legend itemWidth after font patch takes effect.
          alignLegendItems(this)
        },
        redraw: function (this: unknown) {
          if (originalRedraw) (originalRedraw as Function).call(this)
          // Recalculate on resize (PC↔SP) — value comparison inside
          // prevents infinite loop when alignment itself triggers redraw.
          alignLegendItems(this)
        },
      },
    },
    legend: {
      ...legend,
      itemStyle: withChartFontStyle(legend.itemStyle),
    },
    xAxis: withChartAxisFont(options.xAxis),
    yAxis: withChartAxisFont(options.yAxis),
  }
}

const installHighchartsChartFontPatch = () => {
  const highcharts = (
    window as typeof window & { Highcharts?: HighchartsGlobal }
  ).Highcharts
  const OriginalChart = highcharts?.Chart

  if (!highcharts || !OriginalChart || highcharts.chartFontPatchInstalled) {
    return
  }

  const PatchedChart = function (options: Record<string, unknown>) {
    return new OriginalChart(withChartFontOptions(options))
  } as unknown as HighchartsConstructor

  PatchedChart.prototype = OriginalChart.prototype
  highcharts.Chart = PatchedChart
  highcharts.chartFontPatchInstalled = true
}

const handleActionAnchorKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
  if (event.key !== 'Enter' && event.key !== ' ') return

  event.preventDefault()
  event.currentTarget.click()
}

const handlePrintClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
  if (event.defaultPrevented) return

  event.preventDefault()
  window.print()
}

const reflowPanelCharts = (panel: HTMLElement) => {
  const highcharts = (
    window as typeof window & { Highcharts?: HighchartsGlobal }
  ).Highcharts

  highcharts?.charts?.forEach((chart: HighchartsChart | undefined) => {
    if (!chart?.renderTo || !panel.contains(chart.renderTo)) return

    chart.reflow?.()
    chart.redraw?.()
  })
}

const handleChartModeClick = (
  event: ReactMouseEvent<HTMLAnchorElement>,
  mode: 'graph' | 'table'
) => {
  event.preventDefault()
  event.stopPropagation()
  event.nativeEvent.stopImmediatePropagation()

  const panel = event.currentTarget.closest<HTMLElement>('dd')
  if (!panel) return

  panel.querySelectorAll<HTMLElement>('.graph-container').forEach((element) => {
    element.style.display = mode === 'graph' ? '' : 'none'
  })

  panel.querySelectorAll<HTMLElement>('.table-container').forEach((element) => {
    element.style.display = mode === 'table' ? '' : 'none'
  })

  panel.querySelectorAll<HTMLLIElement>('.chartable li').forEach((item) => {
    item.classList.toggle('active', item.contains(event.currentTarget))
  })

  panel
    .querySelectorAll<HTMLAnchorElement>('.chartable a')
    .forEach((anchor) => {
      anchor.classList.toggle('active', anchor === event.currentTarget)
    })

  if (mode === 'graph') {
    window.requestAnimationFrame(() => {
      if (panel.id === 'cate') {
        updateCategoryChart(getCategoryDataKey(panel))
      }

      // table→graph: restore all hidden series and reflow
      const highcharts = (
        window as typeof window & { Highcharts?: HighchartsGlobal }
      ).Highcharts
      highcharts?.charts?.forEach(
        (chart: HighchartsChart | undefined) => {
          if (!chart?.renderTo || !panel.contains(chart.renderTo)) return
          chart.series?.forEach((series: HighchartsSeries | undefined) => {
            if (series?.visible === false) series.setVisible?.(true, false)
          })
        }
      )

      reflowPanelCharts(panel)
    })
  }
}

const isChartSource = (value: unknown): value is ChartSource =>
  isRecord(value)

const getCategoryRuntime = () => {
  const chartWindow = window as ChartRuntimeWindow
  const source = chartWindow.csource
  const categories = chartWindow.cmcategories

  if (!isChartSource(source) || !isStringArray(categories)) return null

  return { chartWindow, source, categories }
}

const getCategoryDataKey = (panel: HTMLElement): CategoryDataKey =>
  panel.dataset.categoryDataKey === 'seiyu' ? 'seiyu' : 'cate'

const addCommas = (value: string | number | null) => {
  if (!value) return ''

  const [integerPart, decimalPart] = String(value).split('.')
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger
}

const getSelectedPeriodIndexes = (
  checkContainer: HTMLElement,
  categories: string[]
) =>
  Array.from(
    checkContainer.querySelectorAll<HTMLInputElement>('.cselect:checked')
  )
    .map((input) => Number(input.value))
    .filter(
      (index) =>
        Number.isInteger(index) && index >= 0 && index < categories.length
    )

const getCategoryChart = () => {
  const highcharts = (
    window as typeof window & { Highcharts?: HighchartsGlobal }
  ).Highcharts

  return highcharts?.charts
    ?.slice()
    .reverse()
    .find(
      (chart: HighchartsChart | undefined): chart is HighchartsChart =>
        !!chart &&
        !chart.destroyed &&
        !!chart.renderTo?.isConnected &&
        getChartRenderToId(chart) === 'graph-cate'
    )
}

const renderCategoryTable = (
  panel: HTMLElement,
  dataset: ChartDataset,
  selectedCategories: string[],
  tableRows: Array<Array<string | number | null>>
) => {
  const tableContainer = panel.querySelector<HTMLElement>('.table')
  if (!tableContainer) return

  const table = document.createElement('table')
  const thead = document.createElement('thead')
  const tbody = document.createElement('tbody')
  const headRow = document.createElement('tr')

  headRow.appendChild(document.createElement('th'))
  selectedCategories.forEach((category) => {
    const th = document.createElement('th')
    th.textContent = category
    headRow.appendChild(th)
  })

  dataset.indexes.forEach(({ name }, rowIndex) => {
    const row = document.createElement('tr')
    const th = document.createElement('th')
    th.textContent = name
    row.appendChild(th)

    tableRows[rowIndex]?.forEach((value) => {
      const td = document.createElement('td')
      td.textContent = addCommas(value)
      row.appendChild(td)
    })

    tbody.appendChild(row)
  })

  thead.appendChild(headRow)
  table.append(thead, tbody)
  tableContainer.replaceChildren(table)
}

const updateCategoryHighcharts = (
  dataset: ChartDataset,
  selectedCategories: string[],
  seriesData: Array<Array<number | null>>
) => {
  const chart = getCategoryChart()
  if (!chart) return false

  chart.update?.(
    {
      chart: { height: dataset.height ?? 500 },
      plotOptions: {
        series: {
          stacking: dataset.stack ? 'normal' : null,
        },
      },
    },
    false,
    false,
    false
  )

  chart.xAxis?.[0]?.setCategories?.(selectedCategories, false)
  chart.xAxis?.[0]?.update?.({ categories: selectedCategories }, false)

  chart.yAxis?.[0]?.update?.(
    {
      min: dataset.min ?? null,
      max: dataset.max ?? null,
      tickInterval: dataset.span ?? null,
      title: {
        y: -20,
        x: 30,
        align: 'high',
        rotation: 0,
        text: dataset.unit ?? '',
      },
    },
    false
  )

  chart.yAxis?.[1]?.update?.(
    {
      min: dataset.min2 ?? null,
      max: dataset.max2 ?? null,
      tickInterval: dataset.span2 ?? null,
      title: {
        y: -20,
        x: 20,
        align: 'high',
        rotation: 0,
        text: dataset.unit2 ?? '',
      },
    },
    false
  )

  seriesData.forEach((data, index) => {
    const chartIndex = dataset.indexes[index]
    if (!chartIndex) return

    chart.series?.[index]?.update?.(
      {
        data,
        name: chartIndex.name,
        type: chartIndex.type,
        color: chartIndex.color,
        yAxis: chartIndex.type === 'column' || chartIndex.yAxis === 0 ? 0 : 1,
      },
      false
    )
  })

  chart.redraw?.()
  chart.reflow?.()

  return true
}

const updateCategoryChart = (dataKey: CategoryDataKey) => {
  const runtime = getCategoryRuntime()
  const panel = document.getElementById('cate')
  const checkContainer = document.getElementById('graph-cate-check')

  if (!runtime || !panel || !checkContainer) return

  const { chartWindow, source, categories } = runtime
  const dataset = source[dataKey]
  if (!dataset) return

  panel.dataset.categoryDataKey = dataKey
  renderPeriodCheckboxes(checkContainer, categories)

  panel.querySelectorAll<HTMLAnchorElement>('#nav a.menu').forEach((anchor) => {
    anchor.classList.toggle(
      'active',
      anchor.getAttribute('href') === `#${dataKey}`
    )
  })

  const selectedIndexes = getSelectedPeriodIndexes(checkContainer, categories)
  const selectedCategories = selectedIndexes.map((index) => categories[index])
  const seriesData = dataset.master.map((row) =>
    selectedIndexes.map((index) => {
      const value = parseFloat(String(row[index]))
      return Number.isNaN(value) ? null : value
    })
  )
  const tableRows = dataset.master.map((row) =>
    selectedIndexes.map((index) => row[index])
  )

  const title = panel.querySelector<HTMLElement>('.title')
  if (title) title.textContent = dataset.title

  panel.querySelectorAll<HTMLElement>('.note').forEach((note) => {
    note.innerHTML = dataset.note ?? ''
  })

  panel.querySelectorAll<HTMLElement>('.graph').forEach((graph) => {
    graph.style.height = `${dataset.height ?? 440}px`
  })

  renderCategoryTable(panel, dataset, selectedCategories, tableRows)

  if (updateCategoryHighcharts(dataset, selectedCategories, seriesData)) return

  const graph = document.getElementById('graph-cate')
  if (
    graph &&
    !graph.querySelector('.highcharts-container') &&
    chartWindow.runChart &&
    chartWindow.jQuery
  ) {
    chartWindow.runChart(
      undefined,
      chartWindow.jQuery(panel),
      source,
      categories,
      'graph-cate',
      dataKey
    )
  }
}

const handleCategoryNavClick = (
  event: ReactMouseEvent<HTMLAnchorElement>,
  dataKey: CategoryDataKey
) => {
  event.preventDefault()
  event.stopPropagation()
  event.nativeEvent.stopImmediatePropagation()
  updateCategoryChart(dataKey)
}

const handleCategoryPeriodClick = (event: ReactMouseEvent<HTMLDivElement>) => {
  const target = event.target as HTMLElement
  const button = target.closest<HTMLInputElement>('input.clast')
  if (!button) return

  const panel = button.closest<HTMLElement>('dd')
  const checkContainer = button.closest<HTMLElement>('.check-container')
  if (panel?.id !== 'cate' || !checkContainer) return

  event.preventDefault()
  event.stopPropagation()
  event.nativeEvent.stopImmediatePropagation()

  const last = Number(button.dataset.val)
  const checkboxes = Array.from(
    checkContainer.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
  )

  checkboxes.forEach((checkbox, index) => {
    checkbox.checked = !(checkboxes.length + last > index)
  })

  updateCategoryChart(getCategoryDataKey(panel))
}

const handleCategoryPeriodChange = (event: FormEvent<HTMLDivElement>) => {
  const target = event.target as HTMLElement
  const checkbox = target.closest<HTMLInputElement>('input.cselect')
  if (!checkbox) return

  const panel = checkbox.closest<HTMLElement>('dd')
  if (panel?.id !== 'cate') return

  event.stopPropagation()
  event.nativeEvent.stopImmediatePropagation()
  updateCategoryChart(getCategoryDataKey(panel))
}

const handleCategoryPeriodNativeClick = (event: MouseEvent) => {
  const target = event.target
  if (!(target instanceof HTMLElement)) return

  const button = target.closest<HTMLInputElement>(
    '#graph-cate-check input.clast'
  )
  if (!button) return

  const panel = button.closest<HTMLElement>('dd')
  const checkContainer = button.closest<HTMLElement>('.check-container')
  if (panel?.id !== 'cate' || !checkContainer) return

  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()

  const last = Number(button.dataset.val)
  const checkboxes = Array.from(
    checkContainer.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
  )

  checkboxes.forEach((checkbox, index) => {
    checkbox.checked = !(checkboxes.length + last > index)
  })

  updateCategoryChart(getCategoryDataKey(panel))
}

const handleCategoryPeriodNativeChange = (event: Event) => {
  const target = event.target
  if (!(target instanceof HTMLElement)) return

  const checkbox = target.closest<HTMLInputElement>(
    '#graph-cate-check input.cselect'
  )
  if (!checkbox) return

  const panel = checkbox.closest<HTMLElement>('dd')
  if (panel?.id !== 'cate') return

  event.stopPropagation()
  event.stopImmediatePropagation()
  updateCategoryChart(getCategoryDataKey(panel))
}

const installCategoryPeriodEventGuard = () => {
  document.addEventListener('click', handleCategoryPeriodNativeClick, true)
  document.addEventListener('change', handleCategoryPeriodNativeChange, true)

  return () => {
    document.removeEventListener('click', handleCategoryPeriodNativeClick, true)
    document.removeEventListener(
      'change',
      handleCategoryPeriodNativeChange,
      true
    )
  }
}

const renderPeriodCheckboxes = (
  checkContainer: HTMLElement,
  categories: string[]
) => {
  if (checkContainer.querySelector('.cselect')) return

  const fragment = document.createDocumentFragment()

  categories.forEach((category, index) => {
    const input = document.createElement('input')
    input.type = 'checkbox'
    input.dataset.val = category
    input.value = String(index)
    input.checked = true
    input.name = `c${category}`
    input.id = `c${category}`
    input.className = 'cselect'

    const label = document.createElement('label')
    label.htmlFor = input.id
    label.textContent = category

    fragment.append(input, label)
  })

  checkContainer.insertBefore(fragment, checkContainer.firstChild)
}

const initializeChartBindings = () => {
  const chartWindow = window as ChartRuntimeWindow
  const { jQuery, runChart } = chartWindow

  if (!jQuery || !runChart) return false

  const chartTargets: Array<ChartTarget | null> = chartSections.map(
    (section) => {
      const panel = document.getElementById(section.id)
      const graph = document.getElementById(section.graphId)
      const checkContainer = document.getElementById(section.checkId)
      const source = chartWindow[section.sourceGlobal]
      const categories = chartWindow[section.categoriesGlobal]

      if (
        !panel ||
        !graph ||
        !checkContainer ||
        !source ||
        !isStringArray(categories)
      ) {
        return null
      }

      return {
        ...section,
        panel,
        graph,
        checkContainer,
        source,
        categories,
      }
    }
  )

  if (chartTargets.some((target) => target === null)) return false

  const targets = chartTargets.filter(isChartTarget)

  // Only generate period checkboxes. Chart rendering and tab switching are
  // handled by main.js $(document).ready and jQuery handlers.
  targets.forEach(({ checkContainer, categories }) => {
    renderPeriodCheckboxes(checkContainer, categories)
  })

  return true
}

const handleChartTabClick = (
  event: ReactMouseEvent<HTMLAnchorElement>,
  _id: string
) => {
  // prevent hash navigation only; tab switching handled by main.js jQuery handler
  event.preventDefault()
}

const useChartScripts = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return

    let isCancelled = false
    const pendingTimeouts: number[] = []
    const clearEmptyHash = () => {
      const hasEmptyHash =
        window.location.hash === '#' ||
        (window.location.hash === '' && window.location.href.endsWith('#'))

      if (!hasEmptyHash) return

      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}`
      )
    }

    clearEmptyHash()
    window.addEventListener('hashchange', clearEmptyHash)
    const cleanupCategoryPeriodEventGuard = installCategoryPeriodEventGuard()

    const scheduleChartBindingInitialization = () => {
      let attemptCount = 0
      const maxAttemptCount = 10

      const tryInitialize = () => {
        if (isCancelled) return

        attemptCount += 1
        if (initializeChartBindings() || attemptCount >= maxAttemptCount) return

        pendingTimeouts.push(window.setTimeout(tryInitialize, 50))
      }

      pendingTimeouts.push(window.setTimeout(tryInitialize, 100))
    }

    const loadScript = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const existingScript = document.querySelector<HTMLScriptElement>(
          `script[data-chart-script="${src}"]`
        )

        if (existingScript) {
          if (existingScript.dataset.loaded === 'true') {
            resolve()
            return
          }

          existingScript.addEventListener('load', () => resolve(), {
            once: true,
          })
          existingScript.addEventListener(
            'error',
            () => reject(new Error(`Failed to load ${src}`)),
            { once: true }
          )
          return
        }

        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = src
        script.async = false
        script.dataset.chartScript = src
        script.onload = () => {
          script.dataset.loaded = 'true'
          resolve()
        }
        script.onerror = () => reject(new Error(`Failed to load ${src}`))
        document.body.appendChild(script)
      })

    const loadStylesheet = (href: string) =>
      new Promise<void>((resolve, reject) => {
        const existingLink = document.querySelector<HTMLLinkElement>(
          `link[rel="stylesheet"][data-chart-stylesheet="${href}"], link[rel="stylesheet"][href="${href}"]`
        )

        if (existingLink) {
          existingLink.dataset.loaded = 'true'
          resolve()
          return
        }

        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = href
        link.dataset.chartStylesheet = href
        link.onload = () => {
          link.dataset.loaded = 'true'
          resolve()
        }
        link.onerror = () => reject(new Error(`Failed to load ${href}`))
        document.body.appendChild(link)
      })

    // main.js jQuery handlers don't trigger reflow after tab switch.
    // Use a native delegated handler to call reflowPanelCharts with double RAF
    // after jQuery finishes showing/hiding panels.
    const tabEl = document.querySelector('.tab')
    const handleTabReflow = (e: Event) => {
      const anchor = (e.target as HTMLElement).closest<HTMLAnchorElement>(
        'a[href^="#"]'
      )
      if (!anchor) return
      const id = anchor.getAttribute('href')?.slice(1)
      if (!id) return
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const panel = document.getElementById(id)
          if (panel) reflowPanelCharts(panel)
        })
      })
    }
    tabEl?.addEventListener('click', handleTabReflow)

    const loadScripts = async () => {
      await loadScript(jqueryScript)
      if (isCancelled) return
      await loadStylesheet(chartStylesheet)
      installChartFontScopeOverride()

      for (const src of chartScripts) {
        if (isCancelled) return
        await loadScript(src)
      }

      // Highcharts loaded globally via _document.tsx — apply font patch now
      installHighchartsChartFontPatch()

      scheduleChartBindingInitialization()
    }

    loadScripts().catch((error) => {
      console.error(error)
    })

    return () => {
      isCancelled = true
      pendingTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId))
      tabEl?.removeEventListener('click', handleTabReflow)
      window.removeEventListener('hashchange', clearEmptyHash)
      cleanupCategoryPeriodEventGuard()

      // Prevent this page's global font-family override from leaking into
      // other pages after client-side navigation away from here.
      document
        .querySelectorAll<HTMLLinkElement>(
          `link[data-chart-stylesheet="${chartStylesheet}"]`
        )
        .forEach((link) => link.remove())
      document.getElementById(chartFontOverrideId)?.remove()
    }
  }, [])
}

const IrpNoticeLink = styled.a`
  &:hover {
    text-decoration: underline;
    color: ${theme.colors.hoverLightBlue};
  }
`

const ChartToolbar = ({ graphLabel }: { graphLabel: string }) => (
  <div id="" className="buttons">
    <div className="text-left">
      <ul className="chartable">
        <li className="active">
          <a
            className="show-graph"
            role="button"
            tabIndex={0}
            onClickCapture={(event) => handleChartModeClick(event, 'graph')}
            onKeyDown={handleActionAnchorKeyDown}
          >
            {graphLabel}
          </a>
        </li>
        <li>
          <a
            className="show-table"
            role="button"
            tabIndex={0}
            onClickCapture={(event) => handleChartModeClick(event, 'table')}
            onKeyDown={handleActionAnchorKeyDown}
          >
            Table
          </a>
        </li>
      </ul>
    </div>
    <div className="text-right">
      <div className="graph-container">
        <ul>
          <li>
            <a
              className="print"
              role="button"
              tabIndex={0}
              onClick={handlePrintClick}
              onKeyDown={handleActionAnchorKeyDown}
            >
              Print
            </a>
          </li>
          <li id="adjst">
            <a href={downloadPath}>Download</a> (
            <a
              href={downloadPath}
              id="download"
              target="_blank"
              rel="noopener noreferrer"
              className="xls-download"
            >
              Excel
            </a>
            )
          </li>
        </ul>
      </div>
      <div className="table-container" style={{ display: 'none' }}>
        <ul>
          <li>
            <a
              className="print"
              role="button"
              tabIndex={0}
              onClick={handlePrintClick}
              onKeyDown={handleActionAnchorKeyDown}
            >
              Print
            </a>
          </li>
          <li id="adjst">
            <a href={downloadPath}>Download</a> (
            <a
              href={downloadPath}
              id="download"
              target="_blank"
              rel="noopener noreferrer"
              className="xls-download"
            >
              Excel
            </a>
            )
          </li>
        </ul>
      </div>
    </div>
  </div>
)

const CategoryNav = () => (
  <div id="nav">
    <ul>
      <li>
        <a
          className="menu active"
          href="#cate"
          style={{ fontWeight: 'bold' }}
          onClickCapture={(event) => handleCategoryNavClick(event, 'cate')}
        >
          TRIAL
        </a>
      </li>
      <li>
        <a
          className="menu"
          href="#seiyu"
          onClickCapture={(event) => handleCategoryNavClick(event, 'seiyu')}
        >
          SEIYU
        </a>
      </li>
    </ul>
  </div>
)

const ChartPanel = ({
  id,
  title,
  graphId,
  checkId,
  buttons,
  graphLabel,
  hasCategoryNav,
}: ChartSection) => (
  <dd id={id} className={`jsTabItem${id === 'newest' ? ' active' : ''}`}>
    <div className="container">
      {hasCategoryNav && <CategoryNav />}
      <ChartToolbar graphLabel={graphLabel} />

      <div className="gt-holder">
        <p id="desc">{description}</p>
        <div className="head-title ">
          <h2 className={`title${id === 'newest' ? '' : ' h2Tit'}`}>{title}</h2>
        </div>
        <div className="graph-container">
          <div className="graph" id={graphId} style={{ margin: '0 auto' }} />
        </div>
        <div
          className="table-container"
          style={{ height: 'auto', margin: '0 auto', display: 'none' }}
        >
          <div className="table table-scroll" style={{ margin: 0 }} />
          <ul className="annotation spOnly">
            <li>
              <span className="number"></span>
              <span></span>
            </li>
          </ul>
        </div>
        <p className="note"></p>
      </div>

      <h2 className="h3Tit">Period</h2>
      <div
        id={checkId}
        className="check-container"
        onClickCapture={id === 'cate' ? handleCategoryPeriodClick : undefined}
        onChangeCapture={id === 'cate' ? handleCategoryPeriodChange : undefined}
      >
        <br />
        <div className="btns" style={{ display: 'none' }}>
          {buttons.map(({ value, label }) => (
            <input
              key={value}
              type="button"
              data-val={value}
              value={label}
              className="clast"
            />
          ))}
        </div>
        {id !== 'newest' && <p id="note"></p>}
      </div>
    </div>
  </dd>
)

export const ChartPage = () => {
  useChartScripts()

  return (
    <Layout>
      <NextHead>
        <link rel="preconnect" href="https://irpocket.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link rel="preload" href={chartStylesheet} as="style" />
      </NextHead>
      <SectionTitle
        enTitleText="Chart Generator"
        subTitleText=""
        color="normal"
      />
      <Spacer h={{ sp: 70, pc: 120 }} />

      <div className="jsTabGroup">
        <ul className="tab col2">
          {chartSections.map(({ id, tabLabel }) => (
            <li key={id} className={id === 'newest' ? 'active' : undefined}>
              <a
                href={`#${id}`}
                className={id === 'newest' ? 'act' : undefined}
                onClick={(event) => handleChartTabClick(event, id)}
              >
                {tabLabel}
              </a>
            </li>
          ))}
        </ul>

        <dl id="fychart">
          <dt style={{ display: 'none' }}>Chart Generator</dt>
          {chartSections.map((section) => (
            <ChartPanel key={section.id} {...section} />
          ))}
        </dl>
      </div>

      <div className="irp_comment">
        <IrpNoticeLink
          href="//irpocket.com/notice_en.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          About IR site
        </IrpNoticeLink>
      </div>
    </Layout>
  )
}

ChartPage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'Home', path: '/en/' },
    { name: 'Investor Relations', path: '/en/ir' },
    { name: 'Financial Information', path: '/en/ir/financial' },
    { name: 'Chart Generator', path: '' },
  ]

  return (
    <>
      <Head
        title="Chart Generator"
        description="The chart generator for financial information."
        ogp={{ url: 'en/ir/financial/chart' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle=""
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}
